import { useCallback, useEffect, useMemo } from 'react';

interface DataToStore {
  moduleId?: string;
  schoolYearId?: string;
  schoolId?: string;
  groupIds: string[];
  selectedTab: 'STUDENT' | 'EXERCISE';
}

interface StorageData extends Omit<DataToStore, 'groupIds'> {
  groupIds: string;
}

export const RESULT_FILTER_LOCAL_STORAGE_KEY = 'resultsFilterData';

// Hook responsible for syncing selected filters with local storage.
export function useFilterInLocalStorageSynchronizer(
  { moduleId, schoolYearId, schoolId, groupIds, selectedTab }: DataToStore,
  superModuleId?: string,
) {
  const sortedConcatenatedGroupIds = useMemo(() => groupIds.sort().join(','), [groupIds]);

  const storageData = useMemo<StorageData>(() => {
    return {
      moduleId,
      schoolYearId,
      schoolId,
      groupIds: sortedConcatenatedGroupIds,
      selectedTab,
    };
  }, [moduleId, schoolId, schoolYearId, selectedTab, sortedConcatenatedGroupIds]);

  useEffect(() => {
    if (!superModuleId || !Object.values(storageData).every(value => value !== undefined && value !== null)) return;

    localStorage.setItem(`${RESULT_FILTER_LOCAL_STORAGE_KEY};${superModuleId}`, JSON.stringify(storageData));
  }, [storageData, superModuleId]);
}

/**
 * @returns a function to retrieve local storage stored data.
 */
export function useFilterInLocalStorage(superModuleId?: string) {
  const getStoredData = useCallback(() => {
    if (!superModuleId) return null;

    const storedData = localStorage.getItem(`${RESULT_FILTER_LOCAL_STORAGE_KEY};${superModuleId}`);

    if (!storedData) return null;

    try {
      const parsedData: StorageData = JSON.parse(storedData);

      return {
        ...parsedData,
        groupIds: parsedData.groupIds.split(','),
      } as DataToStore;
    } catch (error) {
      return null;
    }
  }, [superModuleId]);

  return { getStoredData };
}
